import ListItem from '@mui/material/ListItem';
import MuiListItemButton from '@mui/material/ListItemButton';
import MuiListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import { PageName, pageAtom } from 'src/atoms/pages/pages';
import { PrimitiveAtom, useAtom, useAtomValue } from 'jotai';
import React from 'react';
import { useNavigate } from 'react-router';
import { DEFAULT_BORDER_RADIUS } from 'src/theme/constants';

export type NavigationLinkProps = {
  'data-testid'?: string;
  label: string;
  icon?: React.ReactNode;
  page: PageName;
};

const ListItemButton = styled(MuiListItemButton)(({ selected, theme }) => {
  return {
    borderRight: selected ? '4px solid ' : '',
    marginLeft: theme.spacing(4),
    borderTopLeftRadius: DEFAULT_BORDER_RADIUS,
    borderBottomLeftRadius: DEFAULT_BORDER_RADIUS,
    paddingLeft: theme.spacing(8),
    borderColor: theme.palette.grey[500],
    maxHeight: theme.spacing(40),
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
  };
});

const ListItemIcon = styled(MuiListItemIcon)(({ theme }) => {
  return {
    minWidth: 0,
    marginRight: theme.spacing(8),
    color: theme.palette.text.secondary,
  };
});

export type NavigationLinkBaseProps = {
  'data-testid'?: string;
  label: string;
  icon: React.ReactNode;
  to: string;
  page: PageName;
};

export const NavigationLink = ({ label, icon, to, page, ...props }: NavigationLinkBaseProps) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useAtom(pageAtom);
  const selected = currentPage === page;
  return (
    <ListItem
      disablePadding
      {...props}
      onClick={() => {
        if (to) {
          setCurrentPage(page);
          navigate(to);
        }
      }}
    >
      <ListItemButton selected={selected}>
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText disableTypography sx={{ typography: 'h4' }} primary={label} />
      </ListItemButton>
    </ListItem>
  );
};

export type NavigationLinkWithInnerNavigationSupportProps = Omit<NavigationLinkBaseProps, 'to'> & {
  navigationAtom: PrimitiveAtom<string>;
};

export const NavigationLinkWithInnerNavigationSupport = ({
  navigationAtom,
  ...props
}: NavigationLinkWithInnerNavigationSupportProps) => {
  const url = useAtomValue(navigationAtom);

  return <NavigationLink {...props} to={url} />;
};
